import {Observable, of, OperatorFunction, pipe} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {isSmartSecurityKey, SmartSecurityService} from '../../../components/smart-security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {SubscriptionSubject} from '../../../components/streaming/subscription-streaming.service';
import {Exchange} from '../../../enums/exchange';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage,
    ArrayEncodedMdUpdateMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {Security} from '../../../models/security';
import {formatSecurityKey, parseSecurityKey, SecurityKey} from '../../../models/security-key';
import {Quote} from './quote';
import {QuotesSubscriptionSubject} from './quotes-subscription-subject';

enum SmartQuotesWebSocketMessageType {
    SMART_QUOTES_SNAPSHOT = 'SMART_QUOTES_SNAPSHOT',
    SMART_QUOTES_UPDATE   = 'SMART_QUOTES_UPDATE'
}

interface SmartQuotesWebSocketMessage extends WebSocketMessage {
    messageType: SmartQuotesWebSocketMessageType;
    data: ArrayEncodedMdMessage[];
}

interface SmartQuotesSnapshotWebSocketMessage extends SmartQuotesWebSocketMessage {
    messageType: SmartQuotesWebSocketMessageType.SMART_QUOTES_SNAPSHOT;
    data: ArrayEncodedMdSnapshotMessage[];
}

interface SmartQuotesUpdateWebSocketMessage extends SmartQuotesWebSocketMessage {
    messageType: SmartQuotesWebSocketMessageType.SMART_QUOTES_UPDATE;
    data: ArrayEncodedMdUpdateMessage[];
}

export class SmartQuotesSubscriptionSubject extends QuotesSubscriptionSubject {

    private smartAggregatedMessageTypeToCommonDict: {[aggregatedMessageType: string]: SmartQuotesWebSocketMessageType};

    constructor(
        protected override aggregationScale: number,
        protected smartSecurityService: SmartSecurityService
    ) {
        super(aggregationScale, smartSecurityService);
    }

    protected override createSubjectType(): void {
        this.type = {
            name: `SMART_QUOTES${this.aggregationSuffix}`,
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: `SUBSCRIBE_SMART_QUOTES${this.aggregationSuffix}`,
                UNSUBSCRIBE: `UNSUBSCRIBE_SMART_QUOTES${this.aggregationSuffix}`
            },
            messagePipe: pipe(
                filter((message: WebSocketMessage) =>
                    !!this.smartAggregatedMessageTypeToCommonDict[message.messageType]),

                mergeMap((message: WebSocketMessage): Observable<Quote> => {
                    const quotes: Quote[] = this.readSmartQuotesMessage(message);

                    return of(...quotes);
                })
            ),
            subjectPipeFactory: joinedSubject => this.createSmartSubjectPipe(joinedSubject),
            deprecateSubject: joinedSubject => this.deprecateSmartSubject(joinedSubject)
        };
    }

    protected override updateAggregationSuffix(): void {
        this.aggregationSuffix = this.aggregationScale
            ? `#${this.aggregationScale}`
            : '';
    }

    protected override createWebSocketMessageTypes(): void {
        const snapshotMessage = [SmartQuotesWebSocketMessageType.SMART_QUOTES_SNAPSHOT, this.aggregationSuffix]
            .join('');
        const updateMessage = [SmartQuotesWebSocketMessageType.SMART_QUOTES_UPDATE, this.aggregationSuffix]
            .join('');

        this.smartAggregatedMessageTypeToCommonDict = {
            [snapshotMessage]: SmartQuotesWebSocketMessageType.SMART_QUOTES_SNAPSHOT,
            [updateMessage]:   SmartQuotesWebSocketMessageType.SMART_QUOTES_UPDATE
        };
    }

    protected override resolveReceivedSecurity<T extends SecurityKey | Security>(security: T): T {
        return isSmartSecurityKey(security)
            ? {
                ...security,
                ...this.getSmartSecurityKeyFromJoinedKeyStr(security.symbol)
            }
            : security;
    }

    private readSmartQuotesMessage(message: SmartQuotesWebSocketMessage): Quote[] {
        let quotes: Quote[];
        const msgType = this.smartAggregatedMessageTypeToCommonDict[message.messageType];

        switch (msgType) {

            case SmartQuotesWebSocketMessageType.SMART_QUOTES_SNAPSHOT:
                quotes = this.readQuotesSnapshot(message.data as ArrayEncodedMdSnapshotMessage[]);
                break;

            case SmartQuotesWebSocketMessageType.SMART_QUOTES_UPDATE:
                quotes = this.readQuotesUpdate(message.data as ArrayEncodedMdUpdateMessage[]);
                break;

            default:
                quotes = [];
                break;
        }

        return quotes;
    }

    private createSmartSubjectPipe(joinedSubject: SubscriptionSubject): OperatorFunction<Quote, Quote> {
        const smartSubject = this.convertJoinedSubjectToSmart(joinedSubject);

        return this.createSubjectPipe(smartSubject);
    }

    private deprecateSmartSubject(joinedSubject: SubscriptionSubject): void {
        const smartSubject = this.convertJoinedSubjectToSmart(joinedSubject);

        this.deprecateSubject(smartSubject);
    }

    private convertJoinedSubjectToSmart(joinedSubject: SubscriptionSubject): SubscriptionSubject {
        return joinedSubject.map(
            joinedSecurityKeyStr => formatSecurityKey(
                this.getSmartSecurityKeyFromJoinedKeyStr(joinedSecurityKeyStr)
            )
        );
    }

    private getSmartSecurityKeyFromJoinedKeyStr(joinedSecurityKeyStr: string): SecurityKey {
        const firstSecurityKeyStr = joinedSecurityKeyStr.split(',')[0];

        return {
            ...parseSecurityKey(firstSecurityKeyStr),
            securityExchange: Exchange.SMART
        };
    }

}
